import {
  DECISION_FILE_FORMAT,
  DECISION_FILE_VERSION,
  type DecisionImportIssue,
  decisionImportIssues,
  DecisionSheetFile,
  type DecisionSheetView,
  MAX_DECISION_IMPORT_BYTES,
} from '@apm/contracts';
import { decisionSections } from './model';

/**
 * Tệp JSON `apm-decision-sheet` v1 ở phía web — APM-DSHEET-d, plan §5.2.
 *
 * Validate ở client bằng **đúng** schema server dùng (`DecisionSheetFile` từ
 * contracts): người dán một tệp 32 câu thấy **mọi** lỗi theo đường dẫn ngay
 * trong hộp thoại, sửa một lần, không phải gửi đi rồi nhận về từng lỗi.
 */

export type ParsedDecisionFile =
  | { readonly ok: true; readonly file: DecisionSheetFile }
  | { readonly ok: false; readonly issues: readonly DecisionImportIssue[] };

export interface ParseMessages {
  /** `{{max}}` = trần KB. */
  readonly tooLarge: (maxKb: number) => string;
  /** `{{reason}}` = lỗi cú pháp của `JSON.parse`. */
  readonly notJson: (reason: string) => string;
}

export function parseDecisionFileText(text: string, messages: ParseMessages): ParsedDecisionFile {
  if (new Blob([text]).size > MAX_DECISION_IMPORT_BYTES) {
    return {
      ok: false,
      issues: [
        { path: '', message: messages.tooLarge(Math.round(MAX_DECISION_IMPORT_BYTES / 1024)) },
      ],
    };
  }
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (error) {
    return {
      ok: false,
      issues: [
        { path: '', message: messages.notJson(error instanceof Error ? error.message : '') },
      ],
    };
  }
  const result = DecisionSheetFile.safeParse(raw);
  return result.success
    ? { ok: true, file: result.data }
    : { ok: false, issues: decisionImportIssues(result.error) };
}

/**
 * Sổ đang xem ⇒ tệp JSON v1 (kèm `answers`). Server có `export.json` cho bản
 * hiện tại; hàm này dùng khi đang xem **quá khứ** (`asOf`) — bản tải về phải là
 * đúng thứ trên màn hình, mà `export.json` không nhận mốc.
 *
 * Bỏ trường rỗng thay vì ghi `null`/`""`: tệp xuất rồi nhập lại phải ra **đúng**
 * tệp cũ (test khứ hồi), và schema coi trường vắng là "không có".
 */
export function sheetViewToFile(
  view: Pick<DecisionSheetView, 'title' | 'description' | 'allowOther' | 'groups' | 'questions'>,
): DecisionSheetFile {
  const groupCode = new Map(view.groups.map((group) => [group.id, group.code]));
  const ordered = decisionSections(view, 'all', true).flatMap((section) => section.questions);

  const answers = ordered.flatMap((question) =>
    question.answer
      ? [
          {
            code: question.code,
            key: question.answer.key,
            ...(question.answer.note ? { note: question.answer.note } : {}),
          },
        ]
      : [],
  );

  return {
    format: DECISION_FILE_FORMAT,
    version: DECISION_FILE_VERSION,
    title: view.title,
    ...(view.description ? { description: view.description } : {}),
    allowOther: view.allowOther,
    groups: [...view.groups]
      .sort((a, b) => a.position - b.position)
      .map((group) => ({
        code: group.code,
        title: group.title,
        ...(group.note ? { note: group.note } : {}),
        ...(group.highlight ? { highlight: group.highlight } : {}),
      })),
    questions: ordered.map((question) => {
      const group = question.groupId ? groupCode.get(question.groupId) : undefined;
      return {
        code: question.code,
        ...(group ? { group } : {}),
        title: question.title,
        ...(question.origin ? { origin: question.origin } : {}),
        ...(question.sourceRef ? { sourceRef: question.sourceRef } : {}),
        ...(question.context ? { context: question.context } : {}),
        asks: question.asks,
        ...(question.multiple ? { multiple: true } : {}),
        options: question.options.map((option) => ({
          key: option.key,
          title: option.title,
          ...(option.detail ? { detail: option.detail } : {}),
          ...(option.recommended ? { recommended: true } : {}),
        })),
      };
    }),
    ...(answers.length > 0 ? { answers } : {}),
  };
}

/** Tên tệp tải về: bỏ dấu, ký tự lạ thành `-`. */
export function decisionFileName(title: string): string {
  const slug = title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return `${slug || 'so-chot-phuong-an'}.json`;
}

/**
 * Tải tệp về bằng Blob — không qua S3: tệp nhỏ (≤ 500 KB) và do client dựng,
 * cùng cách `run-panel.tsx` tải fixture.
 */
export function downloadDecisionFile(file: DecisionSheetFile): void {
  const blob = new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = decisionFileName(file.title);
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * Lược đồ rút gọn đọc cho người lẫn cho AI Agent ngoài — đi kèm câu nhờ ở hộp
 * thoại Nhập ("Cách lấy từ AI Agent ngoài", plan §5.3 mục 1). Không dịch: đây là tên
 * trường của định dạng, đổi chữ là tệp không nhập được.
 */
export const DECISION_FILE_SHAPE =
  '{"format": "apm-decision-sheet", "version": 1, "title": string, "description"?: string, ' +
  '"allowOther"?: boolean (mặc định true), ' +
  '"groups"?: [{"code": string ngắn, "title": string, "note"?: string, ' +
  '"highlight"?: string ≤ 60 ký tự — NHÃN CHỮ vd. "Ảnh hưởng KPI / tiền", KHÔNG phải true/false}], ' +
  '"questions": [{"code": string duy nhất vd. "A-01", "group"?: mã nhóm, "title": string, ' +
  '"origin"?: "documented" | "new", "sourceRef"?: string, "context"?: string, "asks"?: string[] ≤ 10, ' +
  '"multiple"?: true — câu cho chọn nhiều đáp án, ' +
  '"options": 2–8 × {"key": một chữ "A"…"W", "title": string, "detail"?: string, ' +
  '"recommended"?: true — tối đa một, KHÔNG dùng ở câu "multiple"}}], ' +
  '"answers"?: [{"code": mã câu, "key": mã phương án hoặc "X" — câu "multiple" thì các mã nối bằng dấu phẩy vd. "A,C,X", ' +
  '"note"?: string — bắt buộc khi có "X"}]}';
